/**
 * Lista editable de objetos: agregar, quitar y reordenar (subir / bajar). Entrada
 * `repeatable-list` del catálogo (13/10/2026): antes LinksFields y CardsFields de
 * People Landing repetían los mismos botones y los mismos `moveItem`/`removeItem` a
 * mano. Nace con las variantes y los links de los packs de Partnerships; las dos
 * copias viejas se migran al tocarlas. El contenido de cada fila lo dibuja quien
 * la usa (`renderItem`); acá solo viven el orden y los botones.
 */
import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { arrayMove } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface RepeatableListProps<T> {
  items: T[];
  onChange: (next: T[]) => void;
  /** Dibuja la fila; `update` mergea un parche en ese ítem. */
  renderItem: (item: T, update: (patch: Partial<T>) => void, index: number) => ReactNode;
  makeEmpty: () => T;
  addLabel: string;
  /** Texto cuando no hay ítems. */
  emptyHint?: string;
  max?: number;
  className?: string;
  itemClassName?: string;
}

export function RepeatableList<T>({ items, onChange, renderItem, makeEmpty, addLabel, emptyHint, max, className, itemClassName }: RepeatableListProps<T>) {
  const { t } = useTranslation();
  const update = (i: number, patch: Partial<T>) => onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const remove = (i: number) => onChange(items.filter((_, j) => j !== i));
  const move = (i: number, dir: -1 | 1) => onChange(arrayMove(items, i, i + dir));
  const canAdd = max == null || items.length < max;
  return (
    <div className={cn('space-y-2', className)}>
      {items.length === 0 && emptyHint && <p className="text-xs text-muted-foreground">{emptyHint}</p>}
      {items.map((item, i) => (
        <div key={i} className={cn('rounded-md border border-border/60 bg-muted/20 p-3 flex gap-2', itemClassName)}>
          <div className="flex-1 min-w-0">{renderItem(item, (patch) => update(i, patch), i)}</div>
          <div className="flex flex-col gap-0.5 shrink-0">
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label={t('common.ui.move_up')} title={t('common.ui.move_up')} className="p-1 rounded hover:bg-muted disabled:opacity-30">
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label={t('common.ui.move_down')} title={t('common.ui.move_down')} className="p-1 rounded hover:bg-muted disabled:opacity-30">
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
            <button type="button" onClick={() => remove(i)} aria-label={t('common.ui.remove')} title={t('common.ui.remove')} className="p-1 rounded hover:bg-muted text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}
      {canAdd && (
        <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => onChange([...items, makeEmpty()])}>
          <Plus className="h-3.5 w-3.5" /> {addLabel}
        </Button>
      )}
    </div>
  );
}
